// components/crm/TasksManagement.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { CalendarIcon, CheckIcon } from '@heroicons/react/24/outline';
import { Button } from '../ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Badge } from '../ui/badge';
import { MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { api } from '../../lib/api';
import { toast } from 'sonner';
import CreateTaskDialog from './dialogs/CreateTaskDialog';
import TaskDetailsDialog from './dialogs/TaskDetailsDialog';

const PAGE_SIZE = 20;

// ─── Row skeleton ─────────────────────────────────────────────────────────────
const RowSkeleton = () => (
  <TableRow>
    {[1, 2, 3, 4, 5, 6, 7, 8].map(i => (
      <TableCell key={i}>
        <div className="h-3.5 rounded-full bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 animate-pulse w-20" />
      </TableCell>
    ))}
  </TableRow>
);

const getStatusColor = (status) => {
  const colors = {
    pending: 'bg-yellow-100 text-yellow-800',
    completed: 'bg-green-100 text-green-800',
    overdue: 'bg-red-100 text-red-800',
    in_progress: 'bg-blue-100 text-blue-800',
  };
  return colors[status] || 'bg-gray-100 text-gray-800';
};

const getPriorityColor = (priority) => {
  const colors = {
    high: 'bg-red-100 text-red-800',
    medium: 'bg-yellow-100 text-yellow-800',
    low: 'bg-green-100 text-green-800',
  };
  return colors[priority] || 'bg-gray-100 text-gray-800';
};

/** Fixed widths — see `.crm-table-badge*` in index.css */
const CRM_TYPE_BADGE = 'crm-table-badge crm-table-badge--type';
const CRM_TASK_STATUS_BADGE = 'crm-table-badge crm-table-badge--status';
const CRM_TASK_PRIORITY_BADGE = 'crm-table-badge crm-table-badge--priority';

const getTypeColor = (type) => {
  const colors = {
    call: 'bg-blue-100 text-blue-800',
    demo: 'bg-purple-100 text-purple-800',
    follow_up: 'bg-orange-100 text-orange-800',
    meeting: 'bg-green-100 text-green-800',
    email: 'bg-sky-100 text-sky-800',
    other: 'bg-gray-100 text-gray-800',
  };
  return colors[type] || 'bg-gray-100 text-gray-800';
};

const formatDateTime = (dateString) =>
  new Date(dateString).toLocaleString('en-US', {
    year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
  });

const isOverdue = (dueDate) => new Date(dueDate) < new Date();

// ── Get current logged-in user id from localStorage ───────────────────────────
const getCurrentUserId = () => {
  try {
    const u = JSON.parse(localStorage.getItem('admin_user') || '{}');
    return u._id || u.id || '';
  } catch { return ''; }
};

export default function TasksManagement({ onDataUpdate }) {
  // ── Data state ──────────────────────────────────────────────────────────────
  const [tasks, setTasks] = useState([]);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);

  // ── Filter state (default: show tasks assigned to me, newest first) ──────────
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [assignedFilter, setAssignedFilter] = useState(getCurrentUserId()); // default = me

  // ── Dialog / selection state ─────────────────────────────────────────────────
  const [selectedTask, setSelectedTask] = useState(null);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isViewDialogOpen, setIsViewDialogOpen] = useState(false);
  const [completingId, setCompletingId] = useState(null);

  // ── Reference data ────────────────────────────────────────────────────────────
  const [leads, setLeads] = useState([]);
  const [users, setUsers] = useState([]);

  // ── Fetch tasks — fully server-side filtered ──────────────────────────────────
  const fetchTasks = useCallback(async (opts = {}) => {
    const resolvedPage = opts.page ?? page;
    const resolvedSearch = opts.search ?? searchTerm;
    const resolvedStatus = opts.statusFilter ?? statusFilter;
    const resolvedType = opts.typeFilter ?? typeFilter;
    const resolvedPriority = opts.priorityFilter ?? priorityFilter;
    const resolvedAssigned = opts.assignedFilter !== undefined ? opts.assignedFilter : assignedFilter;

    try {
      setLoading(true);
      const params = new URLSearchParams();
      params.set('page', resolvedPage);
      params.set('limit', PAGE_SIZE);
      params.set('sortBy', 'createdAt');   // newest tasks first
      params.set('sortOrder', 'desc');

      if (resolvedSearch && resolvedSearch !== '') params.set('search', resolvedSearch);
      if (resolvedStatus && resolvedStatus !== 'all') params.set('status', resolvedStatus);
      if (resolvedType && resolvedType !== 'all') params.set('taskType', resolvedType);
      if (resolvedPriority && resolvedPriority !== 'all') params.set('priority', resolvedPriority);
      if (resolvedAssigned && resolvedAssigned !== 'all') params.set('assignedTo', resolvedAssigned);

      const res = await api.get(`/tasks?${params}`);
      setTasks(res.data.data || []);
      setTotal(res.data.pagination?.total || 0);
      setPages(res.data.pagination?.pages || 1);
    } catch (err) {
      console.error('Error fetching tasks:', err);
      toast.error('Failed to fetch tasks');
    } finally {
      setLoading(false);
    }
  }, [page, searchTerm, statusFilter, typeFilter, priorityFilter, assignedFilter]);

  // ── Reference data loaders ────────────────────────────────────────────────────
  const fetchLeads = async () => {
    try {
      // Only need names + emails for the CreateTaskDialog dropdown — fetch minimal set
      const res = await api.get('/leads?limit=20&sortBy=name&sortOrder=asc');
      setLeads(res.data.data || []);
    } catch (err) {
      console.error('Error fetching leads:', err);
    }
  };

  const fetchUsers = async () => {
    try {
      const res = await api.get('/auth/staff-list');
      setUsers(res.data.users || []);
    } catch (err) {
      console.error('Error fetching users:', err);
    }
  };

  // ── Initial load ──────────────────────────────────────────────────────────────
  useEffect(() => {
    fetchTasks();
    fetchLeads();
    fetchUsers();
  }, []);

  // ── Filter change handlers ────────────────────────────────────────────────────
  const handleSearch = (value) => {
    setSearchTerm(value);
    setPage(1);
    fetchTasks({ search: value, page: 1 });
  };

  const handleStatusFilter = (value) => {
    setStatusFilter(value);
    setPage(1);
    fetchTasks({ statusFilter: value, page: 1 });
  };

  const handleTypeFilter = (value) => {
    setTypeFilter(value);
    setPage(1);
    fetchTasks({ typeFilter: value, page: 1 });
  };

  const handlePriorityFilter = (value) => {
    setPriorityFilter(value);
    setPage(1);
    fetchTasks({ priorityFilter: value, page: 1 });
  };

  const handleAssignedFilter = (value) => {
    setAssignedFilter(value);
    setPage(1);
    fetchTasks({ assignedFilter: value, page: 1 });
  };

  const handlePage = (next) => {
    setPage(next);
    fetchTasks({ page: next });
  };

  // ── Post-mutation refresh ──────────────────────────────────────────────────────
  const refreshAll = () => {
    fetchTasks();
    onDataUpdate();
  };

  const handleTaskCreated = () => { refreshAll(); setIsCreateDialogOpen(false); };
  const handleTaskUpdated = () => { refreshAll(); };

  const handleCompleteTask = async (taskId) => {
    setCompletingId(taskId);
    try {
      await api.put(`/tasks/${taskId}/complete`, {
        status: 'completed',
        outcome: 'completed',
        remarks: 'Task completed',
      });
      toast.success('Task marked as completed');
      refreshAll();
    } catch (err) {
      console.error('Error completing task:', err);
      toast.error('Failed to complete task');
    } finally {
      setCompletingId(null);
    }
  };

  return (
    <div className="space-y-4">
      {/* Header Actions */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-lg font-semibold">Tasks Management</h2>
          <p className="text-sm text-gray-600">Manage and track your follow-up tasks</p>
        </div>
        <CreateTaskDialog
          open={isCreateDialogOpen}
          onOpenChange={setIsCreateDialogOpen}
          leads={leads}
          users={users}
          onSuccess={handleTaskCreated}
        />
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-3 flex-wrap">
            {/* Search */}
            <div className="relative flex-1 min-w-[180px]">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Search by title, lead name, or description…"
                value={searchTerm}
                onChange={(e) => handleSearch(e.target.value)}
                className="pl-10"
              />
            </div>
            {/* Assigned To filter */}
            <Select value={assignedFilter} onValueChange={handleAssignedFilter}>
              <SelectTrigger className="w-full md:w-44">
                <SelectValue placeholder="Assigned To" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={getCurrentUserId()}>👤 Assigned to Me</SelectItem>
                <SelectItem value="all">All Staff</SelectItem>
                {users.map(u => (
                  <SelectItem key={u._id} value={u._id}>{u.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {/* Status filter */}
            <Select value={statusFilter} onValueChange={handleStatusFilter}>
              <SelectTrigger className="w-full md:w-36">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="in_progress">In Progress</SelectItem>
                <SelectItem value="completed">Completed</SelectItem>
                <SelectItem value="overdue">Overdue</SelectItem>
              </SelectContent>
            </Select>
            {/* Type filter */}
            <Select value={typeFilter} onValueChange={handleTypeFilter}>
              <SelectTrigger className="w-full md:w-36">
                <SelectValue placeholder="Type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="call">Call</SelectItem>
                <SelectItem value="demo">Demo</SelectItem>
                <SelectItem value="follow_up">Follow Up</SelectItem>
                <SelectItem value="meeting">Meeting</SelectItem>
                <SelectItem value="email">Email</SelectItem>
                <SelectItem value="other">Other</SelectItem>
              </SelectContent>
            </Select>
            {/* Priority filter */}
            <Select value={priorityFilter} onValueChange={handlePriorityFilter}>
              <SelectTrigger className="w-full md:w-36">
                <SelectValue placeholder="Priority" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Priorities</SelectItem>
                <SelectItem value="high">High</SelectItem>
                <SelectItem value="medium">Medium</SelectItem>
                <SelectItem value="low">Low</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Tasks Table */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div>
              <CardTitle>
                {assignedFilter && assignedFilter !== 'all'
                  ? (assignedFilter === getCurrentUserId() ? 'My Tasks' : `Tasks — ${users.find(u => u._id === assignedFilter)?.name || 'Filtered'}`)
                  : 'All Tasks'}
              </CardTitle>
              <CardDescription>
                {loading ? 'Loading…' : `${total} task${total !== 1 ? 's' : ''} found · newest first`}
              </CardDescription>
            </div>
            {assignedFilter && assignedFilter !== 'all' && (
              <button
                onClick={() => handleAssignedFilter('all')}
                className="text-xs text-blue-600 hover:underline"
              >
                Show all tasks
              </button>
            )}
          </div>
        </CardHeader>
        <CardContent className="px-4 pb-4 sm:px-6">
          <div className="overflow-x-auto rounded-lg border border-gray-100 bg-white/70 shadow-sm">
            <Table className="[&_th]:px-3 [&_th]:py-2.5 [&_td]:px-3 [&_td]:py-2.5">
              <TableHeader>
                <TableRow className="bg-blue-500 hover:bg-blue-500">
                  <TableHead className="text-white font-semibold text-xs">Task</TableHead>
                  <TableHead className="text-white font-semibold text-xs">Lead</TableHead>
                  <TableHead className="text-white font-semibold text-xs">Type</TableHead>
                  <TableHead className="text-white font-semibold text-xs">Status</TableHead>
                  <TableHead className="text-white font-semibold text-xs">Priority</TableHead>
                  <TableHead className="text-white font-semibold text-xs">Due Date</TableHead>
                  <TableHead className="text-white font-semibold text-xs">Assigned To</TableHead>
                  <TableHead className="text-center text-white font-semibold text-xs">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  Array.from({ length: 8 }).map((_, i) => <RowSkeleton key={i} />)
                ) : tasks.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center py-10 text-gray-500 text-sm">
                      No tasks found for this filter
                    </TableCell>
                  </TableRow>
                ) : (
                  tasks.map((task) => (
                    <TableRow key={task._id} className="hover:bg-gray-50">
                      <TableCell>
                        <div>
                          <p className="font-medium text-sm">{task.title || 'Follow-up Task'}</p>
                          <p className="text-xs text-gray-500 line-clamp-2">
                            {task.description || task.remarks || 'No description'}
                          </p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div>
                          <p className="text-sm font-medium">{task.leadId?.name || '—'}</p>
                          <p className="text-xs text-gray-500">{task.leadId?.email}</p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge className={`${CRM_TYPE_BADGE} font-normal capitalize ${getTypeColor(task.taskType)}`}>
                          {(task.taskType || '').replace('_', ' ')}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex max-w-[13rem] flex-col items-start gap-1">
                          <Badge className={`${CRM_TASK_STATUS_BADGE} font-normal capitalize ${getStatusColor(task.status)}`}>
                            {task.status}
                          </Badge>
                          {isOverdue(task.dueDate) && task.status === 'pending' && (
                            <span className="crm-badge-overdue">Overdue</span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge className={`${CRM_TASK_PRIORITY_BADGE} font-normal capitalize ${getPriorityColor(task.priority)}`}>
                          {task.priority}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-start gap-1">
                          <CalendarIcon className="mt-0.5 h-4 w-4 flex-shrink-0 text-gray-400" />
                          <span className={`max-w-[11rem] text-xs leading-snug ${isOverdue(task.dueDate) && task.status === 'pending' ? 'font-medium text-red-600' : 'text-gray-600'}`}>
                            {formatDateTime(task.dueDate)}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <p className="text-xs">{task.assignedTo?.name || 'Unassigned'}</p>
                      </TableCell>
                      <TableCell className="text-center align-middle">
                        <div className="flex flex-wrap items-center justify-center gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => { setSelectedTask(task); setIsViewDialogOpen(true); }}
                            className="h-8 px-2 text-xs"
                          >
                            View
                          </Button>
                          {task.status === 'pending' && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleCompleteTask(task._id)}
                              disabled={completingId === task._id}
                              className="h-8 px-2 text-xs bg-green-50 text-green-700 hover:bg-green-100"
                            >
                              {completingId === task._id ? (
                                <svg className="animate-spin h-3 w-3 mr-1" viewBox="0 0 24 24" fill="none">
                                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                                </svg>
                              ) : (
                                <CheckIcon className="h-3 w-3 mr-1" />
                              )}
                              Done
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>

          {/* Pagination */}
          {pages > 1 && (
            <div className="flex items-center justify-between mt-4 pt-3 border-t">
              <p className="text-xs text-gray-500">
                Showing {((page - 1) * PAGE_SIZE) + 1}–{Math.min(page * PAGE_SIZE, total)} of {total}
              </p>
              <div className="flex items-center gap-1.5">
                <Button size="sm" variant="outline" disabled={page <= 1 || loading}
                  onClick={() => handlePage(1)} className="text-xs h-7">
                  « First
                </Button>
                <Button size="sm" variant="outline" disabled={page <= 1 || loading}
                  onClick={() => handlePage(page - 1)} className="text-xs h-7">
                  ← Prev
                </Button>
                {page > 3 && pages > 5 && (
                  <span className="text-xs text-gray-400 px-1">…</span>
                )}
                {Array.from({ length: Math.min(pages, 5) }, (_, i) => {
                  const p = pages <= 5 ? i + 1 : page <= 3 ? i + 1 : page >= pages - 2 ? pages - 4 + i : page - 2 + i;
                  return (
                    <Button key={p} size="sm"
                      variant={p === page ? 'default' : 'outline'}
                      onClick={() => handlePage(p)}
                      className={`text-xs h-7 w-7 ${p === page ? 'bg-blue-600 text-white' : ''}`}>
                      {p}
                    </Button>
                  );
                })}
                {page < pages - 2 && pages > 5 && (
                  <span className="text-xs text-gray-400 px-1">…</span>
                )}
                <Button size="sm" variant="outline" disabled={page >= pages || loading}
                  onClick={() => handlePage(page + 1)} className="text-xs h-7">
                  Next →
                </Button>
                <Button size="sm" variant="outline" disabled={page >= pages || loading}
                  onClick={() => handlePage(pages)} className="text-xs h-7">
                  Last »
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Task Details Dialog */}
      <TaskDetailsDialog
        task={selectedTask}
        open={isViewDialogOpen}
        onOpenChange={setIsViewDialogOpen}
        users={users}
        onTaskUpdated={handleTaskUpdated}
      />
    </div>
  );
}